import * as Sentry from "@sentry/react-native";
import AsyncStorage, { AsyncStorageStatic } from "@react-native-async-storage/async-storage";

/**
 * Safe wrapper around AsyncStorage that returns null instead of throwing errors.
 * This is useful in web environments where localStorage might not be available
 * (e.g., Safari private mode, iframes, restrictive browser contexts).
 */
export const safeAsyncStorage: AsyncStorageStatic = {
    async getItem(key) {
        try {
            return await AsyncStorage.getItem(key);
        } catch (error) {
            Sentry.captureException(error);
            return null;
        }
    },

    async setItem(key, value) {
        try {
            await AsyncStorage.setItem(key, value);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async removeItem(key) {
        try {
            await AsyncStorage.removeItem(key);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async multiGet(keys) {
        try {
            return await AsyncStorage.multiGet(keys);
        } catch (error) {
            Sentry.captureException(error);
            return keys.map((key) => [key, null] as [string, null]);
        }
    },

    async multiSet(keyValuePairs) {
        try {
            await AsyncStorage.multiSet(keyValuePairs);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async multiRemove(keys) {
        try {
            await AsyncStorage.multiRemove(keys);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async clear() {
        try {
            await AsyncStorage.clear();
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async getAllKeys() {
        try {
            return await AsyncStorage.getAllKeys();
        } catch (error) {
            Sentry.captureException(error);
            return [];
        }
    },

    async mergeItem(key, value) {
        try {
            await AsyncStorage.mergeItem(key, value);
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    flushGetRequests() {
        try {
            AsyncStorage.flushGetRequests();
        } catch (error) {
            Sentry.captureException(error);
        }
    },

    async multiMerge(keyValuePairs) {
        try {
            await AsyncStorage.multiMerge(keyValuePairs);
        } catch (error) {
            Sentry.captureException(error);
        }
    },
};
